import { motion } from "framer-motion";
import { Crosshair, Layers, TrendingUp } from "lucide-react";

const CT_IMG =
  "https://images.unsplash.com/photo-1631563019676-dade0dbdb8fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxicmFpbiUyMHNjYW4lMjBjb21wdXRlcnxlbnwwfHx8fDE3ODkwNzE5MDV8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  {
    icon: Crosshair,
    title: "Tumor segmentation",
    copy: "Voxel-precise boundary mapping of lesions across CT and MRI, exported as structured contours for oncology planning.",
  },
  {
    icon: Layers,
    title: "Staging support",
    copy: "Automated TNM-adjacent measurements and RECIST 1.1 response criteria computed on every follow-up study.",
  },
  {
    icon: TrendingUp,
    title: "Longitudinal tracking",
    copy: "Every lesion is matched against priors. Growth velocity and volumetric doubling time, charted automatically.",
  },
];

export default function Oncology() {
  return (
    <section id="oncology" className="bg-ink py-24 md:py-32 border-t border-slate-200 relative overflow-hidden" data-testid="oncology-section">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-pulse/10 blur-[140px]" aria-hidden="true" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-pulse mb-6"
            >
              Oncology focus
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-10"
            >
              Cancer, caught
              <br />
              <span className="text-pulse">earlier.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-mute text-base leading-relaxed max-w-lg mb-14"
            >
              Stage-I detection changes survival curves. X-Gen's oncology models are trained on 2.4M annotated oncological studies to flag malignant signatures at their earliest visible moment.
            </motion.p>

            <div className="space-y-0">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex gap-6 py-8 border-t border-slate-200 group"
                  data-testid={`oncology-feature-${i}`}
                >
                  <span className="w-11 h-11 shrink-0 border border-slate-200 flex items-center justify-center group-hover:border-pulse group-hover:bg-pulse/10 transition-colors duration-300">
                    <f.icon className="w-5 h-5 text-pulse" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-semibold tracking-tight mb-1.5">{f.title}</h3>
                    <p className="text-mute text-sm leading-relaxed max-w-md">{f.copy}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="md:col-span-5 md:sticky md:top-28"
          >
            <div className="relative border border-slate-200 rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(15,23,42,0.05)]" data-testid="oncology-image-frame">
              <img src={CT_IMG} alt="CT slice montage reviewed by oncology AI" className="w-full h-[420px] object-cover max-h-[80vh]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border border-pulse/70" aria-hidden="true">
                <span className="absolute -top-px -left-px w-4 h-4 border-t-2 border-l-2 border-pulse" />
                <span className="absolute -bottom-px -right-px w-4 h-4 border-b-2 border-r-2 border-pulse" />
              </div>
              <div className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-pulse">
                LESION LOCK // MALIGNANCY P=0.94
              </div>
            </div>
            <div className="grid grid-cols-2 border border-slate-200 border-t-0">
              <div className="p-6 border-r border-slate-200">
                <div className="font-display text-3xl font-bold text-pulse">96.8%</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-2">Specificity</div>
              </div>
              <div className="p-6">
                <div className="font-display text-3xl font-bold text-pulse">14mo</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-2">Earlier median detection</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
